import React from "react";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

import { VideoTextBlock } from "./VideoTextBlock";
import { YouTubeEmbedMedia } from "./YouTubeEmbedMedia";

// Reserved black band at the top so the deck's progress bar + close X sit above
// the embed rather than over the provider's own chrome. Mirrors the split
// video-card layout (SPLIT_TOP_CHROME_PX).
const TOP_CHROME_PX = 64;

/**
 * Swipe-deck card for an embedded video. Embeds are YouTube-only (the player
 * runs through react-youtube via YouTubeEmbedMedia, which owns its iframe so
 * several players coexist + tear down safely in the carousel). Keeps the 16:9
 * player: full-bleed fills the card (best for Shorts), split puts the player
 * over a white text panel (best for landscape). The player publishes controls
 * via controllerRef so the gesture overlay + read-more sheet can drive it.
 */
export function VideoEmbedCard({
  card,
  isActive,
  // True when this is the card immediately AFTER the active one. We mount the
  // player for the active card AND the next card only — YouTube can't reliably
  // initialize 3 embedded players at once (the 3rd hangs on a spinner), so we
  // cap concurrency at 2 while keeping forward swipes instant.
  isNextCard = false,
  onAdvance,
  isMuted,
  onToggleMute,
  onProgress,
  // false = a static preview tile (ScaledCardPreview): poster only, no player.
  interactive = true,
}) {
  const content = card.content || {};
  const embedVideoId = content.embed_video_id;
  const embedStart = content.embed_start_seconds || 0;

  // YouTube thumbnail shown for non-player cards (tiles / far-away), so swiping
  // shows a poster instead of black.
  const posterUrl = embedVideoId
    ? `https://i.ytimg.com/vi/${embedVideoId}/hqdefault.jpg`
    : null;

  const title = content.title || "";
  const caption = content.caption || "";
  const textProps = {
    emoji: content.emoji || null,
    titleSize: content.title_size,
    captionSize: content.caption_size,
    titleBold: content.title_bold,
    titleItalic: content.title_italic,
  };
  const muted = isMuted !== undefined ? isMuted : true;

  const isFullBleed = card.layout_variant === "full_bleed";
  const textAlign = content.text_align || "left";
  const textPosition = content.text_position || "bottom";
  const textAlignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[textAlign];

  // Imperative controller, populated by the mounted player (YouTubeEmbedMedia).
  // Stable wrapper so the gesture overlay + VideoTextBlock can call it; reads the
  // live controller each call.
  const controllerRef = React.useRef(null);
  const controller = React.useMemo(
    () => ({
      isPlaying: () =>
        controllerRef.current ? controllerRef.current.isPlaying() : false,
      play: () => {
        controllerRef.current && controllerRef.current.play();
      },
      pause: () => {
        controllerRef.current && controllerRef.current.pause();
      },
    }),
    []
  );

  // Mount the player for the active card + the next one only (2 max — YouTube
  // can't init a 3rd concurrent player). Other cards show the poster.
  const showPlayer = Boolean(embedVideoId && (isActive || isNextCard));
  // A real interactive player is mounted (gesture overlay + mute only apply
  // then). Tiles render a poster, not a player.
  const hasLivePlayer = interactive && showPlayer;

  const blackFill = (
    <div className="absolute inset-0 flex items-center justify-center bg-black" />
  );
  const poster = posterUrl ? (
    <img
      src={posterUrl}
      alt=""
      className="absolute inset-0 w-full h-full object-cover"
    />
  ) : (
    blackFill
  );

  const mediaFrame = hasLivePlayer ? (
    <YouTubeEmbedMedia
      videoId={embedVideoId}
      startSeconds={embedStart}
      isActive={isActive}
      muted={muted}
      onProgress={onProgress}
      onEnded={onAdvance}
      controllerRef={controllerRef}
    />
  ) : (
    poster
  );

  // Transparent gesture layer over the player. A cross-origin embed swallows
  // pointer/touch events, so a drag that starts on the video never reaches the
  // Embla carousel. This sits above it and does NOT stopPropagation, so the drag
  // bubbles to Embla (swipe works); a no-move tap toggles play/pause. It also
  // blocks taps on YouTube's related-video grid.
  const pointerStartRef = React.useRef(null);
  const togglePlayPause = () => {
    if (controller.isPlaying()) controller.pause();
    else controller.play();
  };
  const gestureOverlay = hasLivePlayer ? (
    <div
      className="absolute inset-0"
      onPointerDown={(e) => {
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={(e) => {
        const start = pointerStartRef.current;
        pointerStartRef.current = null;
        if (!start) return;
        const moved =
          Math.abs(e.clientX - start.x) > 10 ||
          Math.abs(e.clientY - start.y) > 10;
        if (!moved) togglePlayPause();
      }}
    />
  ) : null;

  // Deck-shared mute toggle.
  const muteButton = (posClass) =>
    onToggleMute && hasLivePlayer ? (
      <button
        type="button"
        onClick={onToggleMute}
        aria-label={muted ? "Unmute" : "Mute"}
        className={`absolute ${posClass} w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors z-20`}
      >
        {muted ? (
          <VolumeOffIcon fontSize="small" />
        ) : (
          <VolumeUpIcon fontSize="small" />
        )}
      </button>
    ) : null;

  // FULL BLEED — player fills the card; title/caption overlaid. Best for Shorts.
  if (isFullBleed) {
    const overlayContainerClass = {
      top: "absolute top-0 left-0 right-0 pt-16 pb-16 px-6",
      middle: "absolute inset-0 flex flex-col justify-center px-6",
      bottom: "absolute bottom-0 left-0 right-0 pt-16 pb-12 px-6",
    }[textPosition];
    const overlayGradient = {
      top: "linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
      middle: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55))",
      bottom: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)",
    }[textPosition];
    return (
      <div className="relative w-full h-full overflow-hidden bg-black select-none">
        {mediaFrame}
        {gestureOverlay}
        {muteButton("top-20 right-4")}
        {(title || caption) && (
          <div
            className={`${overlayContainerClass} text-white pointer-events-none ${textAlignClass}`}
            style={{ background: overlayGradient }}
          >
            <VideoTextBlock
              title={title}
              caption={caption}
              {...textProps}
              mediaController={controller}
              isActive={isActive}
            />
          </div>
        )}
      </div>
    );
  }

  // SPLIT (default) — 16:9 player up top, text on a white panel below.
  return (
    <div className="relative w-full h-full flex flex-col bg-black select-none">
      <div className="shrink-0" style={{ height: TOP_CHROME_PX }} />
      <div
        className="relative bg-black overflow-hidden w-full shrink-0"
        style={{ aspectRatio: 16 / 9, maxHeight: "55%" }}
      >
        {mediaFrame}
        {gestureOverlay}
        {muteButton("top-2 right-2")}
      </div>
      {/* The white panel always renders in split so the lower half isn't black
          when there's no title/caption yet. */}
      <div className={`flex-1 relative bg-white min-h-0 ${textAlignClass}`}>
        <div className="absolute inset-0 overflow-hidden flex flex-col justify-start px-6 pt-8 pb-8">
          <VideoTextBlock
            title={title}
            caption={caption}
            onWhite
            {...textProps}
            mediaController={controller}
            isActive={isActive}
          />
        </div>
      </div>
    </div>
  );
}
